import { useState } from "react";
import { CheckCircle2, TrendingUp, ArrowRight, X, ChevronDown } from "lucide-react";
import { detailedCases } from "../data/cases";

const SuccessCaseSection = () => {
  const [expandedIdx, setExpandedIdx] = useState(null);
  const successCases = detailedCases;

  return (
    <section data-testid="success-case-section" className="py-16 md:py-24 bg-[#F8F9FA]">
      <div className="max-w-7xl mx-auto px-5 md:px-12">
        <div className="text-center mb-12">
          <div className="inline-flex items-center gap-2 bg-[#0A2540] text-white px-4 py-2 rounded-full text-sm font-bold mb-6">
            <TrendingUp className="w-4 h-4" />
            成功案例
          </div>
          <h2 className="text-2xl sm:text-3xl font-bold text-[#0A2540] tracking-tight mb-4">
            付完所有費用，業主還多賺多少？
          </h2>
          <p className="text-base text-[#475569] max-w-2xl mx-auto">
            真實案例數據，讓您看見合作後業主做更少、賺更多
          </p>
        </div>

        <div className="space-y-5 md:space-y-8">
          {successCases.map((c, idx) => {
            const isOpen = expandedIdx === idx;
            const extraMonthly = !c.after.special ? (c.after.net - c.before.net) : null;
            return (
            <div key={c.name} className="bg-white rounded-2xl shadow-lg overflow-hidden">
              {/* Case Header */}
              <div className="flex flex-wrap md:flex-nowrap items-center gap-3 md:gap-4 p-4 md:p-6 bg-gradient-to-r from-[#0A2540] to-[#163859]">
                {c.link ? (
                  <a href={c.link} target="_blank" rel="noopener noreferrer" className="flex-shrink-0" aria-label={`前往 ${c.name} 官方頁面`}>
                    <img src={c.image} alt={c.name} className="w-14 h-14 md:w-16 md:h-16 rounded-xl object-cover hover:ring-2 hover:ring-[#FF6B00] transition-all" />
                  </a>
                ) : (
                  <img src={c.image} alt={c.name} className="w-14 h-14 md:w-16 md:h-16 rounded-xl object-cover flex-shrink-0" />
                )}
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    {c.link ? (
                      <a href={c.link} target="_blank" rel="noopener noreferrer" data-testid={`case-link-${c.name}`}
                        className="inline-flex items-center gap-1 group">
                        <h3 className="text-base md:text-xl font-bold text-white group-hover:text-[#FF6B00] transition-colors">{c.name}</h3>
                        <ArrowRight className="w-3.5 h-3.5 md:w-4 md:h-4 text-white/70 group-hover:text-[#FF6B00] transition-colors -rotate-45" />
                      </a>
                    ) : (
                      <h3 className="text-base md:text-xl font-bold text-white">{c.name}</h3>
                    )}
                    <span className="text-[10px] md:text-xs bg-white/20 text-white/90 px-2 py-0.5 rounded-full">{c.type}</span>
                  </div>
                  <div className="text-xs md:text-sm text-white/60 mt-1">{c.months}</div>
                </div>
                <div className="w-full md:w-auto md:text-right order-last md:order-none">
                  <div className="text-lg md:text-2xl font-black text-[#FF6B00]">{c.highlight}</div>
                </div>
              </div>

              {/* Mobile: highlight summary (always visible) */}
              <div className="md:hidden p-5 bg-[#FFFAF5]">
                {!c.after.special ? (
                  <>
                    <div className="text-center">
                      <div className="text-xs text-[#475569] mb-1">付完所有費用後，業主每月多賺</div>
                      <div className="text-4xl font-black text-[#FF6B00] leading-none">
                        +NT$ {extraMonthly.toLocaleString()}
                      </div>
                      <div className="text-xs text-[#475569] mt-2 font-semibold">{c.after.bonusNote || "而且做的事情更輕鬆"}</div>
                    </div>
                    <button
                      type="button"
                      data-testid={`case-expand-${idx}`}
                      onClick={() => setExpandedIdx(isOpen ? null : idx)}
                      className="mt-4 w-full flex items-center justify-center gap-1 text-[#FF6B00] text-sm font-bold py-2 border-t border-[#FF6B00]/20"
                    >
                      {isOpen ? "收起試算" : "查看詳細試算"}
                      <ChevronDown className={`w-4 h-4 transition-transform ${isOpen ? 'rotate-180' : ''}`} />
                    </button>
                  </>
                ) : (
                  <div className="text-center">
                    <div className="text-[3.25rem] leading-[1.05] font-black bg-gradient-to-br from-[#FF6B00] via-[#FF8A3D] to-[#FF6B00] bg-clip-text text-transparent drop-shadow-[0_2px_6px_rgba(255,107,0,0.25)] mb-2 tracking-tight">{c.after.specialBig}</div>
                    <div className="text-sm text-[#0A2540] font-bold">{c.after.specialLabel}</div>
                    <div className="text-xs text-[#475569] mt-1.5">{c.after.specialDesc}</div>
                  </div>
                )}
              </div>

              {/* Before / After - Desktop always, Mobile when expanded */}
              <div className={`${isOpen ? 'block' : 'hidden'} md:grid md:grid-cols-2 md:divide-x divide-slate-200 divide-y md:divide-y-0`}>
                {/* Before */}
                <div className="p-4 md:p-6">
                  <div className="flex items-center gap-2 mb-4">
                    <div className="w-8 h-8 bg-slate-200 rounded-full flex items-center justify-center">
                      <X className="w-4 h-4 text-slate-500" />
                    </div>
                    <div className="text-sm font-bold text-slate-500">合作前・自行經營</div>
                  </div>
                  <div className="text-xs text-[#475569] mb-4 bg-slate-50 rounded-lg p-3">{c.before.desc}</div>
                  {c.before.simple ? (
                    <div className="text-center py-4">
                      <div className="text-xs text-[#475569] mb-1">自營時期・月營收</div>
                      <div className="text-3xl font-black text-slate-500">NT$ {c.before.revenue.toLocaleString()}</div>
                      <div className="text-xs text-slate-400 mt-3">* 業主自行處理行銷、客服、OTA 評價與訂單管理</div>
                    </div>
                  ) : c.before.revenue > 0 ? (
                    <div className="space-y-2 text-sm">
                      <div className="flex justify-between"><span className="text-[#475569]">月營收</span><span className="font-semibold">NT$ {c.before.revenue.toLocaleString()}</span></div>
                      <div className="flex justify-between"><span className="text-[#475569]">管家人事（{c.before.staffLabel || '1.5位'}）</span><span className="text-red-500">-{c.before.staff.toLocaleString()}</span></div>
                      <div className="flex justify-between"><span className="text-[#475569]">物業水電</span><span className="text-red-500">-{c.before.utilities.toLocaleString()}</span></div>
                      {c.before.marketing && (
                        <div className="flex justify-between"><span className="text-[#475569]">{c.before.marketingLabel || '行銷人員'}</span><span className="text-red-500">-{c.before.marketing.toLocaleString()}</span></div>
                      )}
                      {c.before.outsourcing && (
                        <div className="flex justify-between"><span className="text-[#475569]">{c.before.outsourcingLabel || '外包行銷'}</span><span className="text-red-500">-{c.before.outsourcing.toLocaleString()}</span></div>
                      )}
                      <div className="flex justify-between border-t pt-2 mt-2"><span className="font-bold text-[#0A2540]">業主淨收入</span><span className={`font-black text-lg ${c.before.net < 0 ? 'text-red-500' : 'text-slate-500'}`}>NT$ {c.before.net.toLocaleString()}</span></div>
                      <div className="text-xs text-red-400 mt-1">* 業主需自行處理：行銷、客服、接單、OTA、社群、收款…</div>
                    </div>
                  ) : (
                    <div className="text-center py-6 text-slate-400 text-sm">
                      {c.before.narrative || "閒置資產，零收入"}
                    </div>
                  )}
                </div>

                {/* After */}
                <div className="p-4 md:p-6 bg-[#FFFAF5]">
                  <div className="flex items-center gap-2 mb-4">
                    <div className="w-8 h-8 bg-[#FF6B00] rounded-full flex items-center justify-center">
                      <CheckCircle2 className="w-4 h-4 text-white" />
                    </div>
                    <div className="text-sm font-bold text-[#FF6B00]">合作後・人客多接手</div>
                  </div>
                  <div className="text-xs text-[#475569] mb-4 bg-white/70 rounded-lg p-3">{c.after.desc}</div>
                  {!c.after.special ? (
                    <div className="space-y-2 text-sm">
                      <div className="flex justify-between"><span className="text-[#475569]">月營收</span><span className="font-semibold text-green-600">+{c.after.revenue.toLocaleString()}</span></div>
                      <div className="flex justify-between"><span className="text-[#475569]">方案費（月付）</span><span className="text-red-500">-{c.after.fee.toLocaleString()}</span></div>
                      <div className="flex justify-between"><span className="text-[#475569]">訂單抽成 15%</span><span className="text-red-500">-{c.after.commission.toLocaleString()}</span></div>
                      <div className="flex justify-between"><span className="text-[#475569]">管家人事（{c.after.staffLabel || '1.5位'}）</span><span className="text-red-500">-{c.after.staff.toLocaleString()}</span></div>
                      <div className="flex justify-between"><span className="text-[#475569]">物業水電</span><span className="text-red-500">-{c.after.utilities.toLocaleString()}</span></div>
                      <div className="flex justify-between border-t pt-2 mt-2">
                        <span className="font-bold text-[#0A2540]">業主淨收入</span>
                        <span className="font-black text-xl text-[#FF6B00]">NT$ {c.after.net.toLocaleString()}</span>
                      </div>
                      <div className="bg-green-50 border border-green-200 rounded-lg p-3 mt-3 text-center">
                        <span className="text-xs text-[#475569]">付完所有費用後，比自營多賺</span>
                        <div className="text-xl font-black text-green-600 mt-1">+NT$ {(c.after.net - c.before.net).toLocaleString()} / 月</div>
                        <div className="text-xs text-green-600 font-semibold">{c.after.bonusNote || "而且做的事情更輕鬆"}</div>
                      </div>
                    </div>
                  ) : (
                    <div className="text-center py-4">
                      <div className="text-5xl md:text-6xl leading-[1.05] font-black bg-gradient-to-br from-[#FF6B00] via-[#FF8A3D] to-[#FF6B00] bg-clip-text text-transparent drop-shadow-[0_2px_10px_rgba(255,107,0,0.3)] mb-3 tracking-tight">{c.after.specialBig}</div>
                      <div className="text-sm md:text-base text-[#0A2540] font-bold">{c.after.specialLabel}</div>
                      <div className="text-xs md:text-sm text-[#475569] mt-1.5">{c.after.specialDesc}</div>
                    </div>
                  )}
                </div>
              </div>
            </div>
            );
          })}
        </div>

        <div className="mt-8 text-right">
          <p className="text-[11px] md:text-xs text-[#94a3b8] leading-relaxed">
            * 現場運營成本將依據不同場域有所變化，獲利數字僅為粗估；營收數字皆為實際案例結果
          </p>
        </div>
      </div>
    </section>
  );
};


// ROI Timeline Section

export default SuccessCaseSection;
